import type { AiRenderSettings } from './ai-render-settings';
import { DISCLAIMER_SAMPLES } from './ai-render-settings';
import { PrototypeRichTextField } from './prototype-rich-text-field';

/**
 * The Disclaimer settings in the panel's AI slider group: per render whether its AI disclaimer shows, and the text every
 * render shares. Prototype chrome only.
 */
export class PrototypeAiRenderEditor {
    private readonly toggles: ReadonlyArray<HTMLButtonElement>;
    private readonly samples: ReadonlyArray<HTMLButtonElement>;
    private readonly reset: HTMLButtonElement | null;
    private readonly textField: PrototypeRichTextField;
    private readonly onChange: (settings: AiRenderSettings) => void;
    private settings: AiRenderSettings;
    private defaultText: string;

    constructor(element: HTMLElement, settings: AiRenderSettings, defaultText: string, onChange: (settings: AiRenderSettings) => void) {
        const textField = element.querySelector<HTMLElement>('[data-prototype-render-text]');
        if (!textField) {
            throw new Error('AI disclaimer settings markup is incomplete');
        }

        this.settings = settings;
        this.defaultText = defaultText;
        this.onChange = onChange;
        this.toggles = Array.from(element.querySelectorAll<HTMLButtonElement>('[data-prototype-render]'));
        this.samples = Array.from(element.querySelectorAll<HTMLButtonElement>('[data-prototype-text-sample]'));
        this.reset = element.querySelector<HTMLButtonElement>('[data-prototype-render-reset]');
        this.textField = new PrototypeRichTextField(textField, text => this.onChange({ ...this.settings, text: text === this.defaultText ? null : text }));

        for (const toggle of this.toggles) {
            toggle.addEventListener('click', () => this.toggleRender(Number(toggle.getAttribute('data-prototype-render'))));
        }

        for (const button of this.samples) {
            button.addEventListener('click', () => {
                const sample = Object.entries(DISCLAIMER_SAMPLES).find(([key]) => key === button.getAttribute('data-prototype-text-sample'))?.[1];
                this.onChange({ ...this.settings, text: sample ?? null });
            });
        }

        this.reset?.addEventListener('click', () => this.onChange({ ...this.settings, hidden: [] }));
    }

    /** The render number a slide stands for: its angle, the same on every product's slider. */
    public static renderNumber(item: Element): number {
        return Number(/\d+$/.exec(item.getAttribute('data-render-key') ?? '')?.[0] ?? 0);
    }

    /** `defaultText` is the disclaimer in the page's language; `renderCount` is how many renders the slider has, so only theirs show a toggle. */
    public setSettings(settings: AiRenderSettings, defaultText: string, renderCount: number) {
        this.settings = settings;
        this.defaultText = defaultText;
        this.textField.setText(settings.text ?? defaultText, settings.text !== null);

        // A text typed by hand matches no sample, so none is pressed.
        const sample = settings.text === null ? 'design' : Object.entries(DISCLAIMER_SAMPLES).find(([, html]) => html === settings.text)?.[0];
        for (const button of this.samples) {
            button.setAttribute('aria-pressed', (button.getAttribute('data-prototype-text-sample') === sample).toString());
        }

        for (const toggle of this.toggles) {
            const render = Number(toggle.getAttribute('data-prototype-render'));
            toggle.hidden = render > renderCount;
            toggle.setAttribute('aria-pressed', (!settings.hidden.includes(render)).toString());
        }

        if (this.reset) {
            this.reset.disabled = settings.hidden.length === 0;
        }
    }

    private toggleRender(render: number) {
        const others = this.settings.hidden.filter(r => r !== render);
        const hidden = others.length === this.settings.hidden.length ? [...others, render].sort((a, b) => a - b) : others;
        this.onChange({ ...this.settings, hidden });
    }
}
